import { motion } from "framer-motion";
import { Linkedin, Instagram, Check } from "lucide-react";

const PARTNERS = [
  {
    name: "Nawaz Shaikh",
    role: "Founder & Chief Growth Architect",
    tag: "Content & Acquisition Systems",
    bio:
      "Nawaz Shaikh is one of Asia's fastest-growing AI entrepreneurs. His AI-powered client acquisition systems have generated over 3B+ cumulative views, helping experts and enterprise brands transform attention into predictable revenue through content & AI automations.",
    highlights: [
      "Trusted by Leaders & Top Experts",
      "3B+ Organic Views Generated",
      "110+ High-Growth Brands Scaled",
      "AI-Powered Client Acquisition Pioneer",
    ],
    stats: [
      { k: "Organic views generated", v: "3B+" },
      { k: "Founders & brands scaled", v: "110+" },
    ],
    avatar:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_viral-launch-7/artifacts/iv9jxbc2_Untitled%20design.zip%20-%201%20%281%29.webp",
    avatarZoom: 2.4,
    avatarPos: "center 45%",
  },
  {
    name: "Vitesh Kotian",
    role: "Co-Founder & Business Growth Strategist",
    tag: "Offer & Revenue Systems Architect",
    bio:
      "Vitesh Kotian is the architect behind high-converting offers, category-defining launches and scalable revenue systems — helping experts transform their knowledge into predictable, high-growth businesses.",
    highlights: [
      "₹100Cr+ Launch Revenue Generated",
      "250+ High-Converting Webinars",
      "Offer Auditing Specialist",
      "Revenue Systems Strategist",
    ],
    stats: [
      { k: "Launch revenue", v: "₹100Cr+" },
      { k: "Webinars engineered", v: "250+" },
    ],
    avatar:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_viral-launch-7/artifacts/56yboygb_1747910689561.jpeg",
    avatarZoom: 1.7,
    avatarPos: "center 25%",
  },
];

export default function Team() {
  return (
    <section
      id="about"
      data-testid="team-section"
      className="relative py-16 md:py-24 border-t border-[#e5e2d8]"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
          <span className="eyebrow" data-testid="team-eyebrow">
            <span className="eyebrow-dot" />
            About
          </span>
          <h2 className="mt-6 font-semibold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.05] md:whitespace-nowrap">
            <span className="text-gradient">Two Founders.</span>{" "}
            <span className="text-gradient-dim">One Mission.</span>
          </h2>
          <p className="mt-6 text-[#0a0a0a]/60 text-[15px] max-w-xl mx-auto">
            Building the next generation of industry leaders by combining
            AI, content and predictable client acquisition.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {PARTNERS.map((p, i) => (
            <motion.article
              key={p.name}
              data-testid={`partner-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="card-raised card-hover p-6 md:p-8 flex flex-col relative overflow-hidden"
            >
              {/* Ambient glow */}
              <div
                aria-hidden
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(255,255,255,0.07), transparent 70%)",
                }}
              />

              <div className="flex items-start gap-5">
                <div className="relative">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border border-black/10 bg-white">
                    <img
                      src={p.avatar}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: p.avatarPos || "center 22%",
                        transform: `scale(${p.avatarZoom || 1.5})`,
                        transformOrigin: p.avatarOrigin || "center top",
                      }}
                    />
                  </div>
                  <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#f3f1eb] border border-black/15">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-[#0a0a0a] text-xl md:text-2xl font-medium tracking-tight">
                    {p.name}
                  </div>
                  <div className="mt-1 text-[#0a0a0a]/60 text-[13.5px]">
                    {p.role}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-2 badge-pill text-[11px]">
                    <span className="eyebrow-dot" />
                    {p.tag}
                  </span>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <a
                    href="#"
                    aria-label={`${p.name} on LinkedIn`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-black/10 bg-white hover:border-black/15 transition-colors"
                  >
                    <Linkedin className="w-4 h-4 text-[#0a0a0a]/70" strokeWidth={1.6} />
                  </a>
                  <a
                    href="#"
                    aria-label={`${p.name} on Instagram`}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-black/10 bg-white hover:border-black/15 transition-colors"
                  >
                    <Instagram className="w-4 h-4 text-[#0a0a0a]/70" strokeWidth={1.6} />
                  </a>
                </div>
              </div>

              <p className="mt-7 text-[#0a0a0a]/65 text-[14px] leading-relaxed">
                {p.bio}
              </p>

              {p.highlights && (
                <div className="mt-7">
                  <div className="text-[#0a0a0a]/50 text-[11px] tracking-[0.22em] uppercase mb-3">
                    Highlights
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                    {p.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-2.5 text-[#0a0a0a]/80 text-[13px] leading-snug"
                      >
                        <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0a0a0a]/6 border border-[#0a0a0a]/12 shrink-0">
                          <Check className="w-2.5 h-2.5 text-[#0a0a0a]" strokeWidth={3} />
                        </span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 grid grid-cols-2 gap-3">
                {p.stats.map((s) => (
                  <div
                    key={s.k}
                    className="rounded-xl border border-[#e5e2d8] bg-white p-4"
                  >
                    <div className="text-[#0a0a0a] font-semibold text-xl tracking-tight">
                      {s.v}
                    </div>
                    <div className="mt-0.5 text-[#0a0a0a]/45 text-[11.5px] uppercase tracking-wider">
                      {s.k}
                    </div>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
